/**
 * @fileoverview A customizable One-Time Password (OTP) input component that works with react-hook-form.
 * Supports both Time-based One-Time Password (TOTP) and email verification codes.
 */
import { Alert, TextField } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

/**
 * Props for the OTPInput component.
 * @template TFieldValues The type of form values being used with react-hook-form
 * @template TName The type of the field name in the form
 */
interface OTPInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    /** The react-hook-form control object */
    control: Control<TFieldValues>;

    /** The name of the field in the form */
    name: TName;

    /** Optional error message to display */
    errorMessage?: string;

    /** The type of OTP verification being performed */
    type?: 'totp' | 'email';

    /** Whether the form is currently being submitted */
    isSubmitting?: boolean;

    /** Autosubmit, you can set it so that it does not autosubmit. It is true by default */
    autoSubmit?: boolean;
}

/**
 * A component for handling OTP (One-Time Password) input with a single centered input field.
 * Features include auto-submit on completion, paste support, and visual display with greyed dots for missing digits.
 * 
 * @template TFieldValues The type of form values being used with react-hook-form
 * @template TName The type of the field name in the form
 * 
 * @example
 * ```tsx
 * <OTPInput
 *   control={control}
 *   name="otpCode"
 *   type="email"
 *   errorMessage={error?.message}
 *   isSubmitting={isSubmitting}
 * />
 * ```
 */
const OTPInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    control,
    name,
    errorMessage,
    type = 'totp',
    isSubmitting = false,
    autoSubmit = true,
}: OTPInputProps<TFieldValues, TName>) => {

    // Create a ref for the input field
    const inputRef = useRef<HTMLInputElement>(null);

    // Create a ref for the form
    const formRef = useRef<HTMLFormElement>(null);

    // Guards against the native 'input' listener and the React onChange/paste
    // handlers both auto-submitting for the same completed code
    const hasAutoSubmittedRef = useRef(false);

    // Allow auto-submit again once the previous submission has finished
    useEffect(() => {
        if (!isSubmitting) {
            hasAutoSubmittedRef.current = false;
        }
    }, [isSubmitting]);

    // Find the parent form on mount
    useEffect(() => {
        if (inputRef.current) {
            let parent = inputRef.current.parentElement;
            while (parent && parent.tagName !== 'FORM') {
                parent = parent.parentElement;
            }

            if (parent?.tagName === 'FORM') {
                formRef.current = parent as HTMLFormElement;
            }
        }
    }, []);

    // Handle autofill from 2FA apps and password managers
    useEffect(() => {
        const input = inputRef.current;

        if (!input) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const value = target.value;

            // Check if the value was autofilled (6 digits)
            if (value?.length === 6 && /^\d{6}$/.test(value)) {
                // Trigger form submission if autoSubmit is enabled
                if (!isSubmitting && !hasAutoSubmittedRef.current && formRef.current && autoSubmit) {
                    hasAutoSubmittedRef.current = true;
                    setTimeout(() => {
                        formRef.current?.requestSubmit();
                    }, 100);
                }
            }
        };

        // Listen for input events (captures autofill)
        input.addEventListener('input', handleInput);

        return () => {
            input.removeEventListener('input', handleInput);
        };
    }, [isSubmitting, autoSubmit]);

    /**
     * Handles changes to the OTP input field.
     * Validates input as numeric and updates form value.
     * 
     * @param value - The new value entered by the user
     * @param onChange - The react-hook-form onChange handler
     */
    const handleOTPChange = (
        value: string,
        onChange: (value: string) => void
    ) => {
        const newValue = value.replace(/\D/g, '').slice(0, 6);
        onChange(newValue);

        // Submit if 6 digits are entered and not already submitting
        if (newValue.length === 6 && !isSubmitting && !hasAutoSubmittedRef.current && formRef.current && autoSubmit) {
            hasAutoSubmittedRef.current = true;
            formRef.current.requestSubmit();
        }
    };

    /**
     * Handles keyboard events for the OTP input.
     * Ensures backspace always deletes from the end.
     * 
     * @param e - The keyboard event
     * @param currentValue - The current value
     * @param onChange - The react-hook-form onChange handler
     */
    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        currentValue: string,
        onChange: (value: string) => void
    ) => {
        if (e.key === 'Backspace') {
            e.preventDefault();

            // Always delete from the end
            if (currentValue.length > 0) {
                onChange(currentValue.slice(0, -1));
            }
        }
    };

    /**
     * Handles paste events for the OTP input.
     * Extracts numeric digits from the pasted content.
     * 
     * @param e - The clipboard event
     * @param onChange - The react-hook-form onChange handler
     */
    const handleOTPPaste = (
        e: React.ClipboardEvent,
        onChange: (value: string) => void
    ) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, 6);

        onChange(pastedData);

        if (pastedData.length === 6 && !isSubmitting && !hasAutoSubmittedRef.current && formRef.current && autoSubmit) {
            hasAutoSubmittedRef.current = true;
            setTimeout(() => {
                formRef.current?.requestSubmit();
            }, 0);
        }
    };

    return (
        <div className="space-y-10">
            <Alert severity="info">
                {type === 'totp'
                    ? 'Please enter the 6-digit code from your authenticator app.'
                    : 'Please enter the 6-digit code sent to your email.'}
            </Alert>

            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        inputRef={inputRef}
                        {...field}
                        fullWidth
                        placeholder="······"
                        error={!!error || !!errorMessage}
                        helperText={errorMessage || error?.message}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                style: { textAlign: 'center', letterSpacing: '0.5em' },
                            },
                        }}
                        onChange={(e) => handleOTPChange(e.target.value, field.onChange)}
                        onKeyDown={(e) => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, field.value || '', field.onChange)}
                        onPaste={(e) => handleOTPPaste(e as React.ClipboardEvent, field.onChange)}
                    />
                )}
            />
        </div>
    );
};

export default OTPInput;